import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { MessageCircle, AlertCircle, QrCode, Lock, PhoneCall } from 'lucide-react';
import { API } from '../App';
import AppTopHeader from '../components/AppTopHeader';

const ScannerPage = () => {
  const { qrCode } = useParams();
  const navigate = useNavigate();
  const [carData, setCarData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [scannerPhone, setScannerPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [showCallAgain, setShowCallAgain] = useState(false);
  const [pendingDialNumber, setPendingDialNumber] = useState(null);
  const [langTab, setLangTab] = useState('en');

  const getOrCreateScannerId = () => {
    const storageKey = 'sideo_scanner_id';
    const existing = localStorage.getItem(storageKey);
    if (existing && existing.trim()) return existing;

    let generated;
    if (window.crypto?.randomUUID) {
      generated = window.crypto.randomUUID();
    } else {
      generated = `scanner_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
    }
    localStorage.setItem(storageKey, generated);
    return generated;
  };

  const loadCarData = async () => {
    try {
      const response = await axios.get(`${API}/scan/${qrCode}`);
      setCarData(response.data);
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      toast.error(typeof errorDetail === 'string' ? errorDetail : 'Invalid or inactive QR code');
      setTimeout(() => navigate('/'), 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCarData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrCode]);

  const validatePhoneNumber = (phone) => {
    // Remove non-digits
    const cleaned = phone.replace(/\D/g, '');
    // Check 10 digits for India
    if (cleaned.length === 10) return true;
    // Check 12 digits with +91 prefix
    if (cleaned.length === 12 && phone.includes('+91')) return true;
    // Check 11 digits for USA (ignore +1 prefix)
    if (cleaned.length === 11 && phone.includes('+1')) return true;
    return false;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setScannerPhone(value);
    if (value.length > 0 && !validatePhoneNumber(value)) {
      setPhoneError('Enter 10 digits (e.g., 9876543210)');
    } else {
      setPhoneError('');
    }
  };

  const sendQuickMessage = async (type, content) => {
    setSending(true);
    try {
      const scannerId = getOrCreateScannerId();
      await axios.post(`${API}/messages`, {
        qr_code: qrCode,
        message_type: type,
        content,
        scanner_id: scannerId
      });
      toast.success('Message sent to vehicle owner!');
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      if (errorDetail?.upgrade_required) {
        toast.error('The car owner has reached their free message limit. Please ask them to upgrade their sideo account.');
      } else {
        toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to send message');
      }
    } finally {
      setSending(false);
    }
  };

  const callCarOwner = async () => {
    if (!scannerPhone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (phoneError) {
      toast.error(phoneError);
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/call-request`, {
        qr_code: qrCode,
        scanner_phone: scannerPhone,
        consent_to_share_phone: true
      });

      const dialNumber = response?.data?.dial_number;
      if (dialNumber) {
        toast.success('Tap the button below to connect your call.');
        setPendingDialNumber(dialNumber);
      } else {
        toast.success(response?.data?.message || 'You will receive a call now. Answer it to connect with the car owner.');
      }
    } catch (error) {
      const status = error.response?.status;
      const errorDetail = error.response?.data?.detail;
      if (status === 429) {
        toast.warning(typeof errorDetail === 'string' ? errorDetail : 'You have an active session. Your call will reach the last QR you scanned.');
      } else if (status === 400) {
        toast.error('Phone must be 10 digits (e.g., 9876543210)');
      } else if (status === 404) {
        toast.error('QR code not recognized. Please try scanning again.');
      } else {
        toast.error(typeof errorDetail === 'string' ? errorDetail : 'Failed to connect call. Please try again.');
      }
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!carData) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Invalid QR Code</h2>
          <p className="text-slate-600">This QR code is not valid or has been deactivated.</p>
        </div>
      </div>
    );
  }

  const quickMessages = {
    en: [
      { type: 'quick', content: 'Your car is blocking mine', icon: MessageCircle },
      { type: 'quick', content: 'Please move your car', icon: MessageCircle },
      { type: 'quick', content: 'Your headlights are on', icon: MessageCircle },
      { type: 'emergency', content: 'EMERGENCY: Need immediate assistance', icon: AlertCircle },
    ],
    hi: [
      { type: 'quick', content: 'आपकी गाड़ी मेरी गाड़ी के आगे खड़ी है', icon: MessageCircle },
      { type: 'quick', content: 'कृपया अपनी गाड़ी हटा लें', icon: MessageCircle },
      { type: 'quick', content: 'आपकी गाड़ी की हेडलाइट चालू है', icon: MessageCircle },
      { type: 'emergency', content: 'आपातकाल: तुरंत मदद चाहिए', icon: AlertCircle },
    ],
  };
  const activeMessages = quickMessages[langTab];

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <AppTopHeader backLabel="Back" />
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Car Info */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow' }}>
            Contact {carData.car.nickname}
          </h1>
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${
              carData.owner_available ? 'bg-emerald-500' : 'bg-slate-400'
            }`}></div>
            <span className="text-sm text-slate-600">
              Owner is {carData.owner_available ? 'available' : 'currently unavailable'}
            </span>
          </div>
        </div>

        {/* Call Car Owner */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Barlow' }}>
            Call Car Owner
          </h2>
          <p className="text-sm text-slate-500 mb-3">We need your number to connect your call with the vehicle owner.</p>
          <input
            type="tel"
            value={scannerPhone}
            onChange={handlePhoneChange}
            placeholder="Your phone number (10 digits)"
            className={`w-full rounded-lg border p-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all ${
              phoneError ? 'border-red-500 bg-red-50' : 'border-slate-200'
            }`}
            data-testid="scanner-phone-input"
          />
          {phoneError && <p className="text-xs text-red-500 mt-1">{phoneError}</p>}
          <div className="flex space-x-3 mt-4">
            <button
              onClick={callCarOwner}
              disabled={sending || !scannerPhone.trim() || phoneError}
              className="w-full btn-primary flex items-center justify-center"
              data-testid="call-owner-btn"
            >
              <PhoneCall className="w-5 h-5 mr-2" />
              Call Car Owner
            </button>
          </div>
          {pendingDialNumber && (
            <div className="mt-3">
              <a
                href={`tel:${pendingDialNumber}`}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-[#0DB504] hover:bg-[#0a9e03] text-white font-semibold transition"
              >
                <PhoneCall className="w-5 h-5" />
                Tap to Call Owner
              </a>
              <p className="text-xs text-slate-500 mt-2 text-center">Owner didn't answer? Tap again to retry.</p>
            </div>
          )}
        </div>

        {/* Quick Messages */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Barlow' }}>
            Quick Messages
          </h2>
          {/* Language tabs */}
          <div className="flex space-x-2 mb-3">
            <button
              onClick={() => setLangTab('en')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                langTab === 'en'
                  ? 'bg-blue-800 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              English Messages
            </button>
            <button
              onClick={() => setLangTab('hi')}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                langTab === 'hi'
                  ? 'bg-blue-800 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              हिंदी संदेश
            </button>
          </div>
          <div className="space-y-3">
            {activeMessages.map((msg, index) => {
              const Icon = msg.icon;
              return (
                <button
                  key={index}
                  onClick={() => sendQuickMessage(msg.type, msg.content)}
                  disabled={sending}
                  style={{ fontFamily: 'Barlow' }}
                  className={`w-full p-4 rounded-xl border-2 text-left flex items-center space-x-3 transition-all hover:-translate-y-1 ${
                    msg.type === 'emergency'
                      ? 'border-orange-200 bg-orange-50 hover:bg-orange-100 hover:border-orange-300'
                      : 'border-slate-200 bg-white hover:bg-slate-50 hover:border-blue-300'
                  }`}
                  data-testid={`quick-message-${index}`}
                >
                  <Icon className={`w-6 h-6 flex-shrink-0 ${
                    msg.type === 'emergency' ? 'text-orange-600' : 'text-blue-800'
                  }`} />
                  <span className="text-slate-900 font-medium">{msg.content}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Privacy Notice */}
        <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-100">
          <div className="flex items-start space-x-3">
            <Shield className="w-6 h-6 text-blue-800 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-blue-900 mb-2">Your Privacy is Protected</h3>
              <p className="text-sm text-blue-800 leading-relaxed">
                Your phone number is never shared. All communication is temporary and automatically expires. 
                The car owner can only respond through the system.
              </p>
            </div>
          </div>
        </div>

        {/* Marketing CTA - Get Your QR */}
        <div className="mt-6 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-2xl border-2 border-[#0DB504] p-6 shadow-lg">
          <div className="text-center">
            <div className="w-16 h-16 bg-[#0DB504] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <QrCode className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Barlow' }}>
              Want Your Own QR Code?
            </h3>
            <p className="text-slate-600 mb-4 leading-relaxed">
              Activate your <span className="font-bold text-[#0DB504]">sideo</span> QR code and keep your privacy protected.
              No more strangers knocking on your car!
            </p>
            <div className="grid md:grid-cols-3 gap-3 mb-6">
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <Lock className="w-5 h-5 text-[#0DB504] mx-auto mb-1" />
                <p className="text-xs font-medium text-slate-700">100% Private</p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <MessageCircle className="w-5 h-5 text-[#0DB504] mx-auto mb-1" />
                <p className="text-xs font-medium text-slate-700">Instant Contact</p>
              </div>
              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <Shield className="w-5 h-5 text-[#0DB504] mx-auto mb-1" />
                <p className="text-xs font-medium text-slate-700">No App Needed</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="w-full md:w-auto px-8 py-4 bg-[#0DB504] hover:bg-[#0a9e03] text-white font-bold rounded-full transition-all shadow-lg hover:shadow-xl hover:scale-105 text-lg"
              data-testid="get-your-qr-btn"
            >
              Get Your QR Code →
            </button>
            <p className="text-xs text-slate-500 mt-3">
              Takes 2 minutes • Activate and renew yearly
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScannerPage;
